import React from 'react';
import {
  Badge,
  Heading,
  Pane,
  Text,
  IconButton,
  CircleArrowLeftIcon,
  CircleArrowRightIcon,
  Card,
} from 'evergreen-ui';
import { Carousel } from 'react-responsive-carousel';
import styles from './BankCarousel.module.css';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

import { useQueryDashboardBalances, useQueryBanks } from '../../services/api/banksService';
import { formatToMoney } from '../../helpers/moneyFormatter';
import LoadingOverlay from '../../shared/LoadingOverlay';
import { useEffect } from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bankValueActions } from '../../store/bankValue';

function BankCarousel() {
  const companyId = localStorage.getItem('companyId');

  const [totalConsolidated, setTotalConsolidated] = useState(0);
  const [totalNotConsolidated, setTotalNotConsolidated] = useState(0);

  const selectedIndex = useSelector((state) => state.bankValue.selectedIndex);

  const dispatch = useDispatch();

  /* API calls */

  const {
    data: banks = [],
    isLoading: isLoadingBanks,
  } = useQueryBanks(
    // company.data.id,
    companyId,
    {
      revalidateIfStale: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const {
    data: dashboardBalances,
    isLoading: isLoadingDashboardBalances,
    isValidating: isValidatingDashboardBalances,
  } = useQueryDashboardBalances(
    // company.data.id,
    companyId,
    {
      revalidateIfStale: true,
      revalidateOnMount: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const totalBanksBalance = () => {
    if (dashboardBalances?.data?.totals) {
      const consolidated = Number(dashboardBalances.data.totals.consolidated) || 0;
      const pending = Number(dashboardBalances.data.totals.pending) || 0;
      setTotalConsolidated(consolidated);
      setTotalNotConsolidated(pending);
    }
  };

  const banksListHandler = () => {
    const tempBanksObj = {};

    if (dashboardBalances?.data?.accounts) {
      dashboardBalances.data.accounts
        .sort((a, b) => {
          const nameA = (a.name || '').toUpperCase();
          const nameB = (b.name || '').toUpperCase();

          if (nameA < nameB) {
            return -1;
          }

          if (nameA > nameB) {
            return 1;
          }

          return 0;
        })
        .forEach((account, index) => {
          tempBanksObj[account.name || ''] = index + 1;
        });
    }

    dispatch(bankValueActions.setBanksList(tempBanksObj));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    console.log('[BankCarousel] Debug data:', {
      isLoadingBanks,
      isLoadingDashboardBalances,
      banks,
      dashboardBalances,
      banksType: typeof banks,
      dashboardBalancesType: typeof dashboardBalances,
      banksIsArray: Array.isArray(banks),
      dashboardBalancesIsArray: Array.isArray(dashboardBalances),
      banksLength: Array.isArray(banks) ? banks.length : banks?.data?.length,
      dashboardBalancesLength: dashboardBalances?.data?.accounts?.length || 0,
    });

    if (!isLoadingDashboardBalances && !isValidatingDashboardBalances && dashboardBalances) {
      banksListHandler();
      totalBanksBalance();
    }
  }, [isLoadingDashboardBalances, isValidatingDashboardBalances, dashboardBalances]);

  if (isLoadingDashboardBalances) {
    return <LoadingOverlay />;
  }

  return (
    <Card
      className={styles.mainCard}
      background="white"
      display="flex"
      flexWrap="wrap"
      maxWidth="100%"
      minWidth="30%"
      height="100%"
      border="none"
      boxShadow="none"
      outline="none"
      padding={6}
    >
      <Pane
        className={styles.mainPane}
        display="flex"
        flexDirection="column"
        width="100%"
        flex="auto"
      >
        <Pane display="flex" paddingTop={16} paddingX={24} gap={12}>
          <Heading size={600} color="#767676" fontWeight="400">
            Contas
          </Heading>
        </Pane>
        <Carousel
          className={styles.carousel}
          centerMode={true}
          centerSlidePercentage={100}
          showIndicators={false}
          showStatus={false}
          showThumbs={false}
          onChange={(index) => dispatch(bankValueActions.setSelectedIndex(index))}
          selectedItem={selectedIndex}
          showArrows={true}
          swipeable={true}
          labels={{ leftArrow: 'Anterior', rightArrow: 'Próximo' }}
          emulateTouch={true}
          renderArrowPrev={(onClickHandler, hasPrev, label) =>
            hasPrev && (
              <IconButton
                position="absolute"
                zIndex={2}
                bottom={0}
                left={0}
                cursor="pointer"
                type="button"
                appearance="minimal"
                icon={CircleArrowLeftIcon}
                onClick={onClickHandler}
                title={label}
                size="large"
              />
            )
          }
          renderArrowNext={(onClickHandler, hasNext, label) =>
            hasNext && (
              <IconButton
                position="absolute"
                zIndex={2}
                bottom={0}
                right={0}
                cursor="pointer"
                type="button"
                appearance="minimal"
                icon={CircleArrowRightIcon}
                onClick={onClickHandler}
                title={label}
                size="large"
              />
            )
          }
        >
          <Pane
            className={styles.itemsPane}
            display="flex"
            height="fit-content"
            flexWrap="wrap"
            paddingX={24}
            paddingY={15}
          >
            <Pane
              display="flex"
              flexDirection="column"
              textAlign="left"
              flex="auto"
              maxWidth={150}
              textOverflow="ellipsis"
              overflow="hidden"
              whiteSpace="nowrap"
            >
              <Text size={600}>Todas as contas</Text>
            </Pane>
            <Pane
              className={styles.allAccountsPane}
              display="flex"
              justifyContent="start"
              gap={15}
              paddingX={10}
              flexWrap="wrap"
              flex={1}
            >
              <Pane display="flex" flexDirection="column" marginBottom={15} flex={1}>
                <Badge color="red" width="fit-content">
                  Vencido
                </Badge>
                <Heading paddingY={10} size={600} textAlign="left" whiteSpace="nowrap">
                  R$ {formatToMoney(Number(totalNotConsolidated) || 0)}
                </Heading>
              </Pane>

              <Pane display="flex" flexDirection="column" flex={1}>
                <Badge color="green" width="fit-content">
                  Consolidado
                </Badge>
                <Heading paddingY={10} size={600} textAlign="left" whiteSpace="nowrap">
                  R$ {formatToMoney(Number(totalConsolidated) || 0)}
                </Heading>
              </Pane>
            </Pane>
          </Pane>
          {(() => {
            // Use new dashboardBalances structure
            const accountsArray = dashboardBalances?.data?.accounts || [];

            console.log('[BankCarousel] Rendering dashboard balances:', {
              accountsArray,
              accountsLength: accountsArray.length,
            });

            if (accountsArray.length === 0) {
              console.warn('[BankCarousel] No accounts data to render');
              return null;
            }

            return accountsArray
              .sort((a, b) => {
                const nameA = (a.name || '').toUpperCase();
                const nameB = (b.name || '').toUpperCase();

                if (nameA < nameB) {
                  return -1;
                }

                if (nameA > nameB) {
                  return 1;
                }

                return 0;
              })
              .map((account) => {
                console.log('[BankCarousel] Processing account:', {
                  accountId: account.id,
                  accountName: account.name,
                  account,
                });

                const displayName = account.name && account.name.trim() !== '' ? account.name : 'Conta sem nome';
                const safePending = isFinite(Number(account.pending)) ? Number(account.pending) : 0;
                const safeConsolidated = isFinite(Number(account.consolidated)) ? Number(account.consolidated) : 0;

                return (
                  <Pane
                    className={styles.allItemsPane}
                    key={account.id}
                    display="flex"
                    height="fit-content"
                    flexWrap="wrap"
                    paddingX={24}
                    paddingY={15}
                  >
                    <Pane
                      display="flex"
                      flexDirection="column"
                      textAlign="left"
                      flex="auto"
                      maxWidth={150}
                      textOverflow="ellipsis"
                      overflow="hidden"
                      whiteSpace="nowrap"
                    >
                      <Text size={600}>{displayName}</Text>
                      <Text size={300}></Text>
                    </Pane>
                    <Pane
                      className={styles.balancePane}
                      display="flex"
                      flexDirection="column"
                      justifyContent="start"
                      paddingX={10}
                      flexWrap="wrap"
                      flex={1}
                    >
                      <div className="flex">
                        <Pane
                          display="flex"
                          flexDirection="column"
                          flex={1}
                          justifyItems="center"
                          alignItems="center"
                        >
                          <Badge color="red" width="fit-content">
                            Vencido
                          </Badge>
                          <Heading paddingY={10} size={600} textAlign="left" whiteSpace="nowrap">
                            R$ {formatToMoney(safePending)}
                          </Heading>
                        </Pane>
                        <Pane
                          display="flex"
                          flexDirection="column"
                          flex={1}
                          justifyItems="center"
                          alignItems="center"
                        >
                          <Badge color="green" width="fit-content">
                            Consolidado
                          </Badge>
                          <Heading paddingY={10} size={600} textAlign="left" whiteSpace="nowrap">
                            R$ {formatToMoney(safeConsolidated)}
                          </Heading>
                        </Pane>
                      </div>
                    </Pane>
                  </Pane>
                );
              })
              .filter(Boolean);
          })()}
        </Carousel>
      </Pane>
    </Card>
  );
}

export default BankCarousel;
